import { useState, useEffect } from 'react';
import AnimatedSection from './components/AnimatedSection';
import Navbar from './components/Navbar';
import ScreenshotCarousel from './components/ScreenshotCarousel';

const features = [
  { icon: 'ri-user-line', title: 'Multiplayer', description: 'Play with friends and players worldwide' },
  { icon: 'ri-trophy-line', title: 'Tournaments', description: 'Compete in exciting poker tournaments' },
  { icon: 'ri-vip-crown-line', title: 'VIP System', description: 'Exclusive rewards for loyal players' },
  { icon: 'ri-gift-line', title: 'Daily Bonuses', description: 'Free chips and rewards every day' },
  { icon: 'ri-flashlight-line', title: 'Fast Gameplay', description: 'Quick matches and instant action' },
];

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      {/* Navigation */}
      <Navbar isScrolled={isScrolled} />

      {/* ── Hero ── */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/edited_aaf933783a4fe3c91afd48892bad9ac6_4bfe1df1.jpg"
            alt="Hero Background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/25 to-black/70" />
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 pt-28 pb-20">
          <div className="flex flex-col items-center text-center lg:items-start lg:text-left lg:grid lg:grid-cols-2 lg:gap-12">
            <div style={{ opacity: 1, animation: 'heroFadeUp 0.9s cubic-bezier(0.22,1,0.36,1) both' }}>
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-serif font-bold leading-tight mb-6 bg-gradient-to-r from-white via-amber-200 to-amber-400 bg-clip-text text-transparent">
                Experience<br />The Thrill of<br />Texas Hold'em
              </h1>
              <div className="flex flex-wrap gap-3 sm:gap-4 mt-8 justify-center lg:justify-start">
                <a
                  href="https://apps.apple.com/us/app/amagi-poker-texas-holdem/id6749308770"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full font-medium flex items-center gap-2 sm:gap-3 hover:scale-105 transition-transform whitespace-nowrap cursor-pointer text-sm sm:text-base"
                >
                  <i className="ri-apple-fill text-lg sm:text-xl" />
                  <span>App Store</span>
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.GAMEINCAMAGI.POKERAGG2D"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 sm:px-8 py-3 sm:py-4 bg-white/10 backdrop-blur-sm border border-white/30 rounded-full font-medium flex items-center gap-2 sm:gap-3 hover:scale-105 hover:border-amber-400 transition-all whitespace-nowrap cursor-pointer text-sm sm:text-base"
                >
                  <i className="ri-google-play-fill text-lg sm:text-xl text-amber-400" />
                  <span>Google Play</span>
                </a>
              </div>
            </div>
            <div
              className="mt-10 lg:mt-0 lg:text-right"
              style={{ animation: 'heroFadeUp 0.9s 0.2s cubic-bezier(0.22,1,0.36,1) both' }}
            >
              <p className="text-base sm:text-lg lg:text-xl xl:text-2xl font-light text-gray-300 leading-relaxed max-w-lg mx-auto lg:ml-auto lg:mr-0">
                Join millions of players worldwide in the most authentic Texas Hold'em experience. Master your skills, compete in tournaments, and become a poker legend.
              </p>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 animate-bounce">
          <span className="text-xs text-white/40 tracking-widest hidden sm:block">SCROLL</span>
          <i className="ri-arrow-down-line text-amber-400 text-xl" />
        </div>
      </section>

      {/* ── About ── */}
      <section id="about" className="py-16 sm:py-20 lg:py-24 bg-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-12 items-center">
            <AnimatedSection animation="fade-left" className="lg:col-span-2">
              <div className="text-xs font-light text-amber-400 tracking-widest mb-4">ABOUT AMAGI POKER</div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-serif font-bold mb-6 leading-tight">
                The Ultimate<br />Poker Experience
              </h2>
              <div className="w-20 h-0.5 bg-amber-400 mb-6" />
              <p className="text-base sm:text-lg text-gray-400 leading-relaxed">
                Amagi Poker brings the excitement of Las Vegas to your fingertips. With stunning graphics, realistic gameplay, and a vibrant
                community of players, experience poker like never before. Whether you're a beginner or a seasoned pro, our game offers the
                perfect blend of competition and entertainment.
              </p>
            </AnimatedSection>

            <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
              {features.map((feature, index) => (
                <AnimatedSection key={index} animation="fade-up" delay={index * 80} className="h-full">
                  <div className="bg-zinc-800 rounded-2xl sm:rounded-3xl p-4 sm:p-6 flex items-center gap-3 sm:gap-4 hover:bg-zinc-700 hover:-translate-y-1 hover:border hover:border-amber-400/50 transition-all cursor-pointer h-full">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center flex-shrink-0">
                      <i className={`${feature.icon} text-2xl sm:text-3xl text-amber-400`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1 text-sm sm:text-base">{feature.title}</h3>
                      <p className="text-xs sm:text-sm text-gray-400">{feature.description}</p>
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Screenshots ── */}
      <ScreenshotCarousel />

      {/* ── Features ── */}
      <section id="features" className="py-16 sm:py-20 lg:py-24 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <AnimatedSection animation="fade-up" className="mb-12 sm:mb-16 text-center">
            <div className="text-xs font-light text-amber-400 tracking-widest mb-4">FEATURES</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-serif font-bold text-white">
              Unmatched Gaming Experience
            </h2>
          </AnimatedSection>

          {/* Row 1: Large left + tall right */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 sm:gap-5 mb-4 sm:mb-5">
            {/* Live Matches - wide */}
            <AnimatedSection animation="fade-right" delay={0} className="lg:col-span-3">
              <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden min-h-[280px] sm:min-h-[340px] h-full">
                <img
                  src="https://readdy.ai/api/search-image?query=Cinematic%20wide%20shot%20of%20multiple%20real%20players%20sitting%20around%20a%20professional%20Texas%20holdem%20poker%20table%20with%20cards%20chips%20and%20intense%20competitive%20atmosphere%20dramatic%20casino%20lighting%20with%20amber%20golden%20glow%20showing%20live%20multiplayer%20action%20and%20authentic%20gameplay%20environment&width=800&height=480&seq=feat-live-match&orientation=landscape"
                  alt="Live Multiplayer Matches"
                  className="w-full h-full object-cover object-top absolute inset-0"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                <div className="absolute bottom-6 sm:bottom-8 left-6 sm:left-8 right-6 sm:right-8">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-500/90 rounded-full mb-3">
                    <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                    <span className="text-xs font-semibold text-white tracking-wide">LIVE NOW</span>
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2">Real-Time Multiplayer</h3>
                  <p className="text-gray-300 text-sm sm:text-base">Compete with real players worldwide in authentic Texas Hold'em showdowns</p>
                </div>
              </div>
            </AnimatedSection>

            {/* Fair Competition - tall */}
            <AnimatedSection animation="fade-left" delay={100} className="lg:col-span-2">
              <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden min-h-[280px] sm:min-h-[340px] h-full">
                <img
                  src="https://readdy.ai/api/search-image?query=Balanced%20scale%20of%20justice%20with%20poker%20chips%20on%20both%20sides%20representing%20fair%20play%20and%20equal%20competition%20in%20poker%20game%20with%20clean%20dark%20background%20glowing%20amber%20light%20symmetrical%20composition%20symbolizing%20integrity%20and%20transparent%20gaming%20rules&width=560&height=480&seq=feat-fair-play&orientation=portrait"
                  alt="Fair Competition"
                  className="w-full h-full object-cover object-top absolute inset-0"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                <div className="absolute bottom-6 sm:bottom-8 left-6 sm:left-8 right-6 sm:right-8">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-black/50 backdrop-blur-sm rounded-full mb-3 border border-amber-400/40">
                    <i className="ri-shield-check-line text-amber-400 text-sm" />
                    <span className="text-xs font-semibold text-amber-400 tracking-wide">CERTIFIED FAIR</span>
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2">Fair Play Guaranteed</h3>
                  <p className="text-gray-300 text-sm">Certified RNG algorithm ensures every hand is completely fair and unbiased</p>
                </div>
              </div>
            </AnimatedSection>
          </div>

          {/* Row 2: Three equal cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
            {/* Rich Rewards */}
            <AnimatedSection animation="fade-up" delay={0}>
              <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden min-h-[260px] sm:min-h-[300px] h-full">
                <img
                  src="https://readdy.ai/api/search-image?query=Luxurious%20pile%20of%20golden%20poker%20chips%20overflowing%20treasure%20chest%20with%20VIP%20crown%20diamond%20badges%20and%20reward%20tokens%20on%20rich%20dark%20velvet%20background%20with%20warm%20golden%20light%20rays%20creating%20premium%20wealth%20and%20exclusive%20bonus%20atmosphere&width=500&height=420&seq=feat-rewards-v2&orientation=portrait"
                  alt="Rich Rewards"
                  className="w-full h-full object-cover object-top absolute inset-0"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                <div className="absolute bottom-5 sm:bottom-6 left-5 sm:left-6 right-5 sm:right-6">
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-500/80 rounded-full mb-2.5">
                    <i className="ri-vip-crown-line text-white text-xs" />
                    <span className="text-xs font-semibold text-white">VIP REWARDS</span>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-white mb-1.5">Generous Rewards</h3>
                  <p className="text-gray-300 text-xs sm:text-sm">Daily check-ins, VIP exclusive benefits, and massive chip bonuses await</p>
                </div>
              </div>
            </AnimatedSection>

            {/* Tournaments */}
            <AnimatedSection animation="fade-up" delay={120}>
              <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden min-h-[260px] sm:min-h-[300px] h-full">
                <img
                  src="https://readdy.ai/api/search-image?query=Grand%20poker%20tournament%20championship%20scene%20with%20a%20gleaming%20golden%20trophy%20centerpiece%20surrounded%20by%20massive%20stacks%20of%20casino%20chips%20on%20a%20dark%20luxury%20poker%20table%20dramatic%20spotlight%20illuminating%20the%20trophy%20no%20playing%20cards%20visible%20cinematic%20atmosphere%20with%20amber%20golden%20light%20rays%20and%20blurred%20crowd%20of%20players%20in%20background%20conveying%20prestige%20competition%20and%20high%20stakes%20prize%20pool&width=500&height=420&seq=feat-tournament-notrophy-v5&orientation=portrait"
                  alt="Tournaments"
                  className="w-full h-full object-cover object-top absolute inset-0"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                <div className="absolute bottom-5 sm:bottom-6 left-5 sm:left-6 right-5 sm:right-6">
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-black/50 backdrop-blur-sm rounded-full mb-2.5 border border-amber-400/40">
                    <i className="ri-trophy-line text-amber-400 text-xs" />
                    <span className="text-xs font-semibold text-amber-400">TOURNAMENT</span>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-white mb-1.5">Championship Tournaments</h3>
                  <p className="text-gray-300 text-xs sm:text-sm">Multi-table tournaments with fierce competition for glory and massive prize pools</p>
                </div>
              </div>
            </AnimatedSection>

            {/* Texas Variants */}
            <AnimatedSection animation="fade-up" delay={240}>
              <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden min-h-[260px] sm:min-h-[300px] h-full">
                <img
                  src="https://public.readdy.ai/ai/img_res/edited_abdd30132414c14f93dd5e539b129a9b_9e8f2682.jpg"
                  alt="Texas Variants"
                  className="w-full h-full object-cover object-top absolute inset-0"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                <div className="absolute bottom-5 sm:bottom-6 left-5 sm:left-6 right-5 sm:right-6">
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-black/50 backdrop-blur-sm rounded-full mb-2.5 border border-amber-400/40">
                    <i className="ri-stack-line text-amber-400 text-xs" />
                    <span className="text-xs font-semibold text-amber-400">MULTI-MODE</span>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-white mb-1.5">Multiple Game Modes</h3>
                  <p className="text-gray-300 text-xs sm:text-sm">Cash games, Sit & Go, Short Deck and more - find your perfect match</p>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* ── Download ── */}
      <section id="download" className="relative py-20 sm:py-28 lg:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Wide%20panoramic%20view%20of%20professional%20poker%20tournament%20table%20with%20multiple%20players%20cards%20and%20chips%20in%20luxurious%20casino%20setting%20with%20dramatic%20lighting%20and%20cinematic%20atmosphere%20showing%20competitive%20gaming%20environment&width=1920&height=800&seq=download-bg&orientation=landscape"
            alt="Download Background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/50" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-12 text-center lg:text-left">
            <AnimatedSection animation="fade-right">
              <h2 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold mb-6 sm:mb-8 lowercase leading-tight">
                download &<br />start playing
              </h2>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <a
                  href="https://apps.apple.com/us/app/amagi-poker-texas-holdem/id6749308770"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 sm:gap-4 px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full hover:scale-105 transition-transform cursor-pointer text-sm sm:text-base"
                >
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-black flex items-center justify-center flex-shrink-0">
                    <i className="ri-apple-fill text-white text-lg sm:text-xl" />
                  </div>
                  <span className="font-medium whitespace-nowrap">Get it on App Store</span>
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.GAMEINCAMAGI.POKERAGG2D"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 sm:gap-4 px-6 sm:px-8 py-3 sm:py-4 bg-white/10 backdrop-blur-sm border border-white/40 rounded-full hover:scale-105 hover:border-amber-400 transition-all cursor-pointer text-sm sm:text-base"
                >
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-black flex items-center justify-center flex-shrink-0">
                    <i className="ri-google-play-fill text-amber-400 text-lg sm:text-xl" />
                  </div>
                  <span className="font-medium whitespace-nowrap">Get it on Google Play</span>
                </a>
              </div>
            </AnimatedSection>

            <AnimatedSection animation="fade-left" className="max-w-md">
              <p className="text-lg sm:text-xl lg:text-2xl font-light leading-relaxed text-gray-200">
                Available on iOS &amp; Android.<br />
                Start your poker journey today.
              </p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-zinc-900 py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 sm:gap-8 mb-10 sm:mb-16">
            {[
              { title: 'Support', links: [{ label: 'Contact Us', href: 'https://t.me/AmagiPoker' }] },
              { title: 'Legal', links: [{ label: 'Privacy Policy', href: '/privacy' }, { label: 'Terms of Service', href: '/terms' }, { label: 'License', href: '/license' }] },
              { title: 'Download', links: [{ label: 'App Store', href: 'https://apps.apple.com/us/app/amagi-poker-texas-holdem/id6749308770' }, { label: 'Google Play', href: 'https://play.google.com/store/apps/details?id=com.GAMEINCAMAGI.POKERAGG2D' }] },
            ].map((col) => (
              <div key={col.title}>
                <h4 className="text-white font-semibold mb-3 sm:mb-4 pb-2 border-b border-amber-400/30 text-sm sm:text-base">
                  {col.title}
                </h4>
                <ul className="space-y-2 sm:space-y-3">
                  {col.links.map((link) => (
                    <li key={link.label}>
                      <a
                        href={link.href}
                        target={link.href.startsWith('http') ? '_blank' : undefined}
                        rel={link.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                        className="text-gray-500 hover:text-amber-400 transition-colors cursor-pointer text-xs sm:text-sm"
                      >
                        {link.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between pt-6 sm:pt-8 border-t border-zinc-800 gap-5 sm:gap-0">
            <div className="text-3xl sm:text-4xl font-black bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">
              AMAGI POKER
            </div>
            <div className="flex items-center gap-4 sm:gap-5">
              <a
                href="https://t.me/AmagiPoker"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#229ED9]/15 border border-[#229ED9]/30 text-[#229ED9] hover:bg-[#229ED9]/25 transition-colors cursor-pointer text-sm whitespace-nowrap"
              >
                <i className="ri-telegram-fill text-lg" />
                <span>Support</span>
              </a>
              <a
                href="https://apps.apple.com/us/app/amagi-poker-texas-holdem/id6749308770"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center text-amber-400 hover:text-amber-300 transition-colors cursor-pointer"
              >
                <i className="ri-apple-fill text-lg sm:text-xl" />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.GAMEINCAMAGI.POKERAGG2D"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center text-amber-400 hover:text-amber-300 transition-colors cursor-pointer"
              >
                <i className="ri-google-play-fill text-lg sm:text-xl" />
              </a>
            </div>
          </div>

          <div className="text-center mt-6 sm:mt-8">
            <p className="text-gray-600 text-xs sm:text-sm">© 2025 Amagi Game Inc. All Rights Reserved</p>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes heroFadeUp {
          from { opacity: 0; transform: translateY(36px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}